<template>
  <main class="courses-admin-page">
    <div class="courses-admin-shell">
      <MarketingToolHeader
        eyebrow="Well come FIT Akademie"
        title="Kursverwaltung"
        description="Plane Mitarbeiterkurse, verwalte Kursformate und behalte Buchungen sowie Wartelisten im Blick."
        icon="mdi-calendar-edit">
        <template v-slot:actions>
          <v-btn icon class="icon-action" aria-label="Zurück zum Kursbereich" title="Zurück zum Kursbereich" @click="$router.push('/kurse')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-btn outlined color="#58d388" class="secondary-action" @click="$router.push('/employee-courses')">
            <v-icon left>mdi-eye-outline</v-icon>
            Kursplan
          </v-btn>
          <v-btn icon class="icon-action" aria-label="Daten aktualisieren" title="Daten aktualisieren" :loading="loading" @click="fetchData">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
      </MarketingToolHeader>

      <!-- Tabs Navigation -->
      <nav class="admin-navigation" aria-label="Bereiche der Kursverwaltung">
        <v-tabs v-model="activeTab" grow dark background-color="transparent" color="#58d388">
          <v-tab href="#timeslots" class="text-capitalize font-weight-bold"> <v-icon left>mdi-calendar-clock</v-icon> Kursplanungen </v-tab>
          <v-tab href="#formats" class="text-capitalize font-weight-bold"> <v-icon left>mdi-format-list-bulleted</v-icon> Kursformate </v-tab>
        </v-tabs>
      </nav>

      <v-tabs-items v-model="activeTab" style="background-color: transparent">
        <!-- TAB 1: TIMESLOTS (COURSES EXECUTION) -->
        <v-tab-item value="timeslots">
          <!-- Header -->
          <section class="section-toolbar">
            <div class="section-heading">
              <span class="section-icon"><v-icon>mdi-calendar-clock-outline</v-icon></span>
              <div>
                <span class="section-eyebrow">Planung</span>
                <h2>Kurse & Lektionen</h2>
                <p>Verwalte Termine, Belegung und Anmeldungen in einer zentralen Übersicht.</p>
              </div>
            </div>
            <div class="section-actions">
              <v-btn outlined color="#58d388" class="secondary-action" @click="openSchedulePdfDialog">
                <v-icon left>mdi-file-pdf-box</v-icon> Einsatzplan PDF
              </v-btn>
              <v-btn color="#009640" class="white--text primary-action" @click="openCreateDialog" :disabled="activeFormats.length === 0">
                <v-icon left>mdi-plus</v-icon> Kurs erfassen
              </v-btn>
            </div>
          </section>

          <!-- Alert if no formats exist -->
          <v-alert v-if="activeFormats.length === 0" type="warning" outlined dense class="mb-6">
            Bevor du einen Kurs planen kannst, musst du im Reiter "Kursformate" mindestens ein aktives Kursformat erfassen!
          </v-alert>

          <!-- Stats Cards -->
          <section class="metric-grid" aria-label="Übersicht der Kursplanung">
            <v-card dark flat class="metric-card">
              <span class="metric-icon icon-green"><v-icon>mdi-calendar-check-outline</v-icon></span>
              <div>
                <span>Geplante Kurse</span>
                <strong>{{ activeCoursesCount }}</strong>
                <small>aktive Termine</small>
              </div>
            </v-card>

            <v-card dark flat class="metric-card">
              <span class="metric-icon icon-calendar"><v-icon>mdi-account-check-outline</v-icon></span>
              <div>
                <span>Buchungen</span>
                <strong>{{ totalRegistrationsCount }}</strong>
                <small>angemeldete Mitarbeitende</small>
              </div>
            </v-card>

            <v-card dark flat class="metric-card">
              <span class="metric-icon icon-waiting"><v-icon>mdi-timer-sand</v-icon></span>
              <div>
                <span>Warteliste</span>
                <strong>{{ totalWaitlistCount }}</strong>
                <small>wartende Mitarbeitende</small>
              </div>
            </v-card>
          </section>

          <!-- DESKTOP VIEW: Grouped daily tables -->
          <div class="d-none d-md-block">
            <v-card dark flat class="table-card">
              <!-- Header Controls -->
              <div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-space-between mb-4 gap-3">
                <div class="d-flex align-center gap-4">
                  <h3 class="text-h6 font-weight-bold white--text mr-4">Kursplanungen</h3>
                  <v-tabs
                    v-model="timeslotsSubTab"
                    dense
                    dark
                    background-color="transparent"
                    color="#58d388"
                    slider-color="#58d388"
                    style="max-width: 300px">
                    <v-tab href="#upcoming" class="text-capitalize font-weight-bold">Anstehend</v-tab>
                    <v-tab href="#past" class="text-capitalize font-weight-bold">Vergangen</v-tab>
                  </v-tabs>
                </div>
              </div>

              <!-- List of Grouped Daily Tables -->
              <div v-if="filteredGroupedCourses.length === 0" class="text-center py-12 empty-state rounded-2xl">
                <v-icon size="48" color="grey darken-2" class="mb-2">mdi-calendar-remove</v-icon>
                <div class="grey--text subtitle-1">Keine Kurse für diese Auswahl gefunden.</div>
              </div>
              <div v-else>
                <div v-for="group in filteredGroupedCourses" :key="group.date" class="mb-6">
                  <!-- Date Header -->
                  <div class="d-flex align-center mb-3">
                    <span class="date-header-dot mr-2"></span>
                    <span class="body-1 font-weight-black white--text">{{ group.date }}</span>
                  </div>

                  <v-data-table
                    :headers="desktopHeaders"
                    :items="group.courses"
                    hide-default-footer
                    disable-pagination
                    class="custom-table border-thin-outline"
                    dark>
                    <!-- Time Format -->
                    <template v-slot:item.dateTime="{ item }">
                      <div>
                        <span class="body-2 white--text font-weight-bold">{{ formatTime(item.dateTime) }} Uhr</span>
                        <span class="caption grey--text d-block">{{ item.duration }} Min. Dauer</span>
                      </div>
                    </template>

                    <!-- Format Title & Details -->
                    <template v-slot:item.title="{ item }">
                      <div>
                        <span class="body-2 font-weight-bold white--text">{{ item.format ? item.format.title : "Unbekannt" }}</span>
                        <span v-if="item.details" class="caption yellow--text text--darken-2 d-block font-italic">{{ item.details }}</span>
                      </div>
                    </template>

                    <!-- Studio -->
                    <template v-slot:item.studio="{ item }">
                      <span class="body-2">{{ item.studio ? item.studio.name : "WELL COME FIT" }}</span>
                    </template>

                    <!-- Registered Spots -->
                    <template v-slot:item.belegung="{ item }">
                      <div>
                        <span
                          class="body-2 font-weight-bold"
                          :class="item.registeredCount >= item.capacity ? 'yellow--text text--darken-2' : 'white--text'">
                          {{ item.registeredCount }} / {{ item.capacity }}
                        </span>
                        <v-chip x-small v-if="item.registeredCount >= item.capacity" color="orange" class="black--text font-weight-black ml-1"
                          >VOLL</v-chip
                        >
                      </div>
                    </template>

                    <!-- Waitlist -->
                    <template v-slot:item.waitlist="{ item }">
                      <div>
                        <span v-if="item.waitlistEnabled" class="body-2"> {{ item.waitlistCount }} wartend </span>
                        <v-chip x-small v-else outlined color="grey">Inaktiv</v-chip>
                      </div>
                    </template>

                    <!-- Status -->
                    <template v-slot:item.status="{ item }">
                      <v-chip x-small :color="item.status === 'active' ? 'green' : 'red'" outlined class="font-weight-bold">
                        {{ item.status === "active" ? "Aktiv" : "Abgesagt" }}
                      </v-chip>
                    </template>

                    <!-- Actions -->
                    <template v-slot:item.actions="{ item }">
                      <div class="d-flex justify-end gap-1">
                        <v-btn icon color="blue" small @click="openManageRegistrations(item)">
                          <v-icon small>mdi-account-multiple</v-icon>
                        </v-btn>
                        <v-btn icon color="green" small @click="openEditDialog(item)" :disabled="item.status === 'cancelled' || isPastCourse(item)">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          color="red"
                          small
                          @click="confirmCancelCourse(item)"
                          :disabled="item.status === 'cancelled' || isPastCourse(item)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </div>
              </div>
            </v-card>
          </div>

          <!-- MOBILE + TABLET VIEW -->
          <div class="d-block d-md-none">
            <!-- Mobile Filters and Controls -->
            <div class="mb-4 d-flex flex-column gap-3">
              <v-tabs
                v-model="timeslotsSubTab"
                dense
                dark
                background-color="transparent"
                color="#58d388"
                slider-color="#58d388"
                class="width-100 mobile-subtabs">
                <v-tab href="#upcoming" class="text-capitalize font-weight-bold">Anstehend</v-tab>
                <v-tab href="#past" class="text-capitalize font-weight-bold">Vergangen</v-tab>
              </v-tabs>

              <div class="d-flex justify-start align-center">
                <v-btn-toggle v-model="mobileViewMode" mandatory dense class="rounded-lg">
                  <v-btn value="calendar" small class="px-3 font-weight-bold text-capitalize">
                    <v-icon left small>mdi-calendar</v-icon> Kalender
                  </v-btn>
                  <v-btn value="list" small class="px-3 font-weight-bold text-capitalize">
                    <v-icon left small>mdi-format-list-bulleted</v-icon> Liste
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>

            <!-- Calendar Mode -->
            <div v-if="mobileViewMode === 'calendar'">
              <v-date-picker
                v-model="mobileCalendarDate"
                full-width
                dark
                color="#009640"
                header-color="#1c1c1e"
                class="rounded-xl mb-4"
                locale="de-CH"
                first-day-of-week="1"
                :events="getCalendarEvents"
                event-color="amber"></v-date-picker>

              <div class="subtitle-2 white--text mb-3 font-weight-black d-flex align-center">
                <v-icon small color="#58d388" class="mr-1">mdi-calendar-check</v-icon>
                Planungen am {{ formatLocalDateString(mobileCalendarDate) }}
              </div>

              <!-- Timeslot list on selected day -->
              <div v-if="mobileSelectedDayCourses.length === 0" class="text-center py-8 empty-state-mobile rounded-xl">
                <v-icon color="grey darken-2" size="48">mdi-calendar-blank</v-icon>
                <div class="caption grey--text mt-2">Keine Lektionen für diesen Tag geplant.</div>
              </div>
              <div v-else class="mobile-course-list">
                <div
                  v-for="course in mobileSelectedDayCourses"
                  :key="course._id"
                  class="mobile-course-card pa-4 rounded-xl relative-box"
                  :class="{ 'is-cancelled': course.status === 'cancelled', 'is-past': isPastCourse(course) }">
                  <div class="d-flex justify-space-between align-start mb-2">
                    <div>
                      <span class="caption font-weight-black text-uppercase yellow--text text--darken-2">
                        {{ course.studio ? course.studio.name : "WELL COME FIT" }}
                      </span>
                      <h4 class="text-subtitle-1 font-weight-black white--text leading-tight mt-0-5">
                        {{ course.format ? course.format.title : "Kurs" }}
                      </h4>
                      <span v-if="course.details" class="caption font-italic grey--text d-block">{{ course.details }}</span>
                    </div>
                    <v-chip x-small :color="course.status === 'active' ? 'green' : 'red'" outlined class="font-weight-bold">
                      {{ course.status === "active" ? "Aktiv" : "Abgesagt" }}
                    </v-chip>
                  </div>

                  <div class="divider-thin my-2"></div>

                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon x-small color="#58d388" class="mr-1">mdi-clock-outline</v-icon>
                      <span class="caption font-weight-bold white--text"> {{ formatTime(course.dateTime) }} Uhr ({{ course.duration }}m) </span>
                    </div>

                    <div class="d-flex align-center">
                      <v-icon x-small color="#58d388" class="mr-1">mdi-account-group-outline</v-icon>
                      <span class="caption font-weight-bold white--text"> {{ course.registeredCount }} / {{ course.capacity }} Plätze </span>
                    </div>
                  </div>

                  <div class="d-flex justify-space-between align-center pt-1">
                    <span class="caption grey--text"> Warteliste: {{ course.waitlistEnabled ? `${course.waitlistCount} wartend` : "Inaktiv" }} </span>

                    <div class="d-flex gap-2">
                      <v-btn icon color="blue" small outlined class="rounded-lg" @click="openManageRegistrations(course)">
                        <v-icon small>mdi-account-multiple</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="green"
                        small
                        outlined
                        class="rounded-lg"
                        @click="openEditDialog(course)"
                        :disabled="course.status === 'cancelled' || isPastCourse(course)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="red"
                        small
                        outlined
                        class="rounded-lg"
                        @click="confirmCancelCourse(course)"
                        :disabled="course.status === 'cancelled' || isPastCourse(course)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- List Mode -->
            <div v-else>
              <div v-if="visibleCoursesFiltered.length === 0" class="text-center py-12 empty-state-mobile rounded-xl">
                <v-icon color="grey darken-2" size="48">mdi-calendar-remove</v-icon>
                <div class="caption grey--text mt-2">Keine Kursplanungen gefunden.</div>
              </div>
              <div v-else class="mobile-course-list">
                <div
                  v-for="course in visibleCoursesFiltered"
                  :key="course._id"
                  class="mobile-course-card pa-4 rounded-xl relative-box"
                  :class="{ 'is-cancelled': course.status === 'cancelled', 'is-past': isPastCourse(course) }">
                  <div class="d-flex justify-space-between align-start mb-2">
                    <div>
                      <span class="caption font-weight-black text-uppercase yellow--text text--darken-2">
                        {{ course.studio ? course.studio.name : "WELL COME FIT" }}
                      </span>
                      <h4 class="text-subtitle-1 font-weight-black white--text leading-tight mt-0-5">
                        {{ course.format ? course.format.title : "Kurs" }}
                      </h4>
                      <span v-if="course.details" class="caption font-italic grey--text d-block">{{ course.details }}</span>
                    </div>
                    <v-chip x-small :color="course.status === 'active' ? 'green' : 'red'" outlined class="font-weight-bold">
                      {{ course.status === "active" ? "Aktiv" : "Abgesagt" }}
                    </v-chip>
                  </div>

                  <div class="divider-thin my-2"></div>

                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon x-small color="#58d388" class="mr-1">mdi-calendar</v-icon>
                      <span class="caption font-weight-bold white--text">
                        {{ formatDateShortCompact(course.dateTime) }}
                      </span>
                    </div>

                    <div class="d-flex align-center">
                      <v-icon x-small color="#58d388" class="mr-1">mdi-clock-outline</v-icon>
                      <span class="caption font-weight-bold white--text"> {{ formatTime(course.dateTime) }} Uhr ({{ course.duration }}m) </span>
                    </div>

                    <div class="d-flex align-center">
                      <v-icon x-small color="#58d388" class="mr-1">mdi-account-group-outline</v-icon>
                      <span class="caption font-weight-bold white--text"> {{ course.registeredCount }} / {{ course.capacity }} </span>
                    </div>
                  </div>

                  <div class="d-flex justify-space-between align-center pt-1">
                    <span class="caption grey--text"> Warteliste: {{ course.waitlistEnabled ? `${course.waitlistCount} wartend` : "Inaktiv" }} </span>

                    <div class="d-flex gap-2">
                      <v-btn icon color="blue" small outlined class="rounded-lg" @click="openManageRegistrations(course)">
                        <v-icon small>mdi-account-multiple</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="green"
                        small
                        outlined
                        class="rounded-lg"
                        @click="openEditDialog(course)"
                        :disabled="course.status === 'cancelled' || isPastCourse(course)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="red"
                        small
                        outlined
                        class="rounded-lg"
                        @click="confirmCancelCourse(course)"
                        :disabled="course.status === 'cancelled' || isPastCourse(course)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-tab-item>

        <!-- TAB 2: COURSE FORMATS (VORLAGEN) -->
        <v-tab-item value="formats">
          <!-- Header -->
          <section class="section-toolbar">
            <div class="section-heading">
              <span class="section-icon"><v-icon>mdi-format-list-bulleted</v-icon></span>
              <div>
                <span class="section-eyebrow">Vorlagen</span>
                <h2>Kursformate</h2>
                <p>Pflege wiederverwendbare Titel und Beschreibungen für deine Kursplanung.</p>
              </div>
            </div>
            <div class="section-actions">
              <v-btn color="#009640" class="white--text primary-action" @click="openCreateFormatDialog">
                <v-icon left>mdi-plus</v-icon> Format erstellen
              </v-btn>
            </div>
          </section>

          <!-- Formats Card / Table -->
          <v-card dark flat class="table-card format-table-card d-none d-md-block">
            <v-data-table :headers="formatHeaders" :items="formats" class="custom-table" :items-per-page="10" dark>
              <!-- Status -->
              <template v-slot:item.active="{ item }">
                <v-chip x-small :color="item.active ? 'green' : 'grey'" outlined class="font-weight-bold">
                  {{ item.active ? "Aktiv" : "Ausgeblendet" }}
                </v-chip>
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <div class="d-flex justify-end">
                  <v-btn icon color="green" small @click="openEditFormatDialog(item)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>

          <div class="format-card-list d-grid d-md-none">
            <v-card v-for="format in formats" :key="format._id" dark flat class="format-card">
              <div class="format-card-header">
                <span class="format-icon"><v-icon>mdi-bookmark-box-multiple-outline</v-icon></span>
                <v-chip small :color="format.active ? 'green' : 'grey'" outlined class="font-weight-bold">
                  {{ format.active ? "Aktiv" : "Ausgeblendet" }}
                </v-chip>
              </div>
              <h3>{{ format.title }}</h3>
              <p>{{ format.description || "Keine Standard-Beschreibung hinterlegt." }}</p>
              <v-btn outlined color="#58d388" class="secondary-action width-100" @click="openEditFormatDialog(format)">
                <v-icon left>mdi-pencil-outline</v-icon>
                Bearbeiten
              </v-btn>
            </v-card>

            <div v-if="formats.length === 0" class="empty-state-mobile">
              <v-icon color="grey darken-1" size="44">mdi-format-list-bulleted-square</v-icon>
              <strong>Noch keine Kursformate</strong>
              <span>Erstelle zuerst eine Vorlage für deine Kurse.</span>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Schedule PDF Export Dialog -->
    <v-dialog
      v-model="schedulePdfDialog.show"
      max-width="560px"
      persistent
      dark
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition">
      <v-card class="dialog-surface pa-6 rounded-2xl">
        <div class="d-flex align-center justify-space-between mb-4">
          <div>
            <span class="caption yellow--text text--darken-2 font-weight-black text-uppercase">PDF-Export</span>
            <h2 class="text-h5 font-weight-black white--text italic-title">Einsatzplan herunterladen</h2>
          </div>
          <v-btn icon color="grey" :disabled="schedulePdfDialog.downloading" @click="schedulePdfDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <p class="grey--text body-2 mb-4">
          Wähle den ersten und letzten Einsatztag. Das PDF enthält alle aktiven Kurse in diesem Zeitraum mit Zeit, Kursdetails und Studio.
        </p>

        <v-date-picker
          v-model="schedulePdfDialog.dates"
          range
          full-width
          dark
          color="#009640"
          header-color="#111111"
          locale="de-CH"
          first-day-of-week="1"
          class="rounded-xl"></v-date-picker>

        <v-alert v-if="schedulePdfDateRange" :type="schedulePdfRangeError ? 'warning' : 'info'" outlined dense class="caption mt-4 mb-0">
          <template v-if="schedulePdfRangeError">{{ schedulePdfRangeError }}</template>
          <template v-else
            >Gewählter Zeitraum: <strong>{{ schedulePdfRangeLabel }}</strong></template
          >
        </v-alert>

        <div class="d-flex justify-end gap-3 mt-5">
          <v-btn text color="grey" :disabled="schedulePdfDialog.downloading" @click="schedulePdfDialog.show = false"> Abbrechen </v-btn>
          <v-btn
            color="#009640"
            class="white--text primary-action"
            :disabled="!isSchedulePdfRangeValid"
            :loading="schedulePdfDialog.downloading"
            @click="downloadSchedulePdf">
            <v-icon left>mdi-download</v-icon> PDF herunterladen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Course Dialog -->
    <v-dialog
      v-model="courseDialog.show"
      max-width="600px"
      persistent
      dark
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition">
      <v-card class="dialog-surface pa-6 rounded-2xl">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="text-h5 font-weight-black white--text italic-title">
            <v-icon color="#58d388" class="mr-2">{{ courseDialog.isEdit ? "mdi-pencil" : "mdi-plus-box" }}</v-icon>
            {{ courseDialog.isEdit ? "Kurs bearbeiten" : "Kurs erfassen" }}
          </h2>
          <v-btn icon color="grey" @click="courseDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form ref="courseForm" v-model="courseDialog.valid">
          <v-row dense>
            <v-col cols="12">
              <v-autocomplete
                dark
                v-model="courseFormModel.format"
                :items="activeFormats"
                item-text="title"
                item-value="_id"
                label="Kursformat auswählen*"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Kursformat ist erforderlich']"
                class="rounded-lg search-field"></v-autocomplete>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="courseFormModel.details"
                label="Spezielle Details für diese Ausführung (z.B. Trainer, optional)"
                outlined
                dense
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                dark
                v-model="courseFormModel.studio"
                :items="studioSelectOptions"
                label="Studio (Standort)*"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Studio ist erforderlich']"
                class="rounded-lg search-field"></v-select>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="courseFormModel.duration"
                label="Dauer (in Minuten)*"
                type="number"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Dauer ist erforderlich', (v) => v > 0 || 'Dauer muss grösser als 0 sein']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="courseFormModel.dateTime"
                label="Datum & Startzeit*"
                type="datetime-local"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Datum und Uhrzeit erforderlich']"
                class="rounded-lg search-field"
                @input="onDateTimeInput"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="courseFormModel.cancellationDeadline"
                label="Abmeldefrist (Standard: 2h vorher)*"
                type="datetime-local"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Abmeldefrist ist erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="courseFormModel.capacity"
                label="Kapazität (Standard 20)*"
                type="number"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Kapazität erforderlich', (v) => v > 0 || 'Kapazität muss grösser als 0 sein']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" class="d-flex align-center pl-4">
              <v-checkbox
                v-model="courseFormModel.waitlistEnabled"
                label="Warteliste aktivieren"
                color="#58d388"
                hide-details
                class="mt-0"></v-checkbox>
            </v-col>
          </v-row>

          <v-alert type="info" outlined dense color="#58d388" class="caption mt-3 dark-alert" v-if="courseDialog.isEdit">
            Wichtig: Falls du die Kapazität erhöhst, werden Personen auf der Warteliste automatisch vom System nachnominiert.
          </v-alert>
        </v-form>

        <div class="d-flex justify-end gap-3 mt-4">
          <v-btn text color="grey" @click="courseDialog.show = false">Abbrechen</v-btn>
          <v-btn
            color="#009640"
            class="white--text primary-action"
            :disabled="!courseDialog.valid"
            :loading="courseDialog.saving"
            @click="submitCourseForm">
            Speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Format Dialog -->
    <v-dialog
      v-model="formatDialog.show"
      max-width="500px"
      persistent
      dark
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition">
      <v-card class="dialog-surface pa-6 rounded-2xl">
        <div class="d-flex align-center justify-space-between mb-4">
          <h2 class="text-h5 font-weight-black white--text italic-title">
            <v-icon color="#58d388" class="mr-2">{{ formatDialog.isEdit ? "mdi-pencil" : "mdi-plus-box" }}</v-icon>
            {{ formatDialog.isEdit ? "Kursformat bearbeiten" : "Kursformat erstellen" }}
          </h2>
          <v-btn icon color="grey" @click="formatDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form ref="formatForm" v-model="formatDialog.valid">
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="formatFormModel.title"
                label="Format-Name*"
                outlined
                dense
                required
                :rules="[(v) => !!v || 'Name ist erforderlich']"
                class="rounded-lg search-field"></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formatFormModel.description"
                label="Standard-Beschreibung"
                outlined
                dense
                rows="4"
                class="rounded-lg search-field"></v-textarea>
            </v-col>

            <v-col cols="12" class="d-flex align-center pl-2" v-if="formatDialog.isEdit">
              <v-checkbox
                v-model="formatFormModel.active"
                label="Format aktiv (anzeigen in Auswahlliste)"
                color="#58d388"
                hide-details
                class="mt-0"></v-checkbox>
            </v-col>
          </v-row>

          <v-alert type="info" outlined dense color="#58d388" class="caption mt-3 dark-alert">
            Hinweis: Kursformate können nicht gelöscht werden, um bestehende Timeslots nicht zu beschädigen. Du kannst sie jedoch ausblenden
            (deaktivieren).
          </v-alert>
        </v-form>

        <div class="d-flex justify-end gap-3 mt-4">
          <v-btn text color="grey" @click="formatDialog.show = false">Abbrechen</v-btn>
          <v-btn
            color="#009640"
            class="white--text primary-action"
            :disabled="!formatDialog.valid"
            :loading="formatDialog.saving"
            @click="submitFormatForm">
            Speichern
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Manage Registrations Dialog -->
    <v-dialog
      v-model="regManager.show"
      max-width="850px"
      persistent
      dark
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition">
      <v-card class="dialog-surface pa-6 rounded-2xl">
        <div class="d-flex align-center justify-space-between mb-4">
          <div>
            <span class="caption yellow--text text--darken-2 font-weight-black text-uppercase">Anmeldungsverwaltung</span>
            <h2 class="text-h5 font-weight-black white--text italic-title" v-if="regManager.course">
              {{ regManager.course.format ? regManager.course.format.title : "Kurs" }}
            </h2>
            <span v-if="regManager.course && regManager.course.details" class="caption font-italic grey--text">{{ regManager.course.details }}</span>
          </div>
          <v-btn icon color="grey" @click="regManager.show = false" :disabled="regManager.loading">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-row class="mb-4">
          <!-- Manual Registration Search Subcard -->
          <v-col cols="12" md="6" class="pa-2">
            <v-card class="pa-4 rounded-xl border-accent-box" dark style="background-color: rgba(255, 255, 255, 0.02); height: 100%">
              <h4 class="subtitle-2 font-weight-black yellow--text text--darken-2 text-uppercase mb-2">Mitarbeiter manuell hinzufügen</h4>
              <!-- User Search Component -->
              <user-search
                class="pa-0 search-field rounded-lg mb-3"
                rights="gf_fetchEmployeeCourses"
                label="Suche (Vorname, Nachname, E-Mail)..."
                @update="onUserSelected"></user-search>

              <v-slide-y-transition>
                <div
                  v-if="regManager.selectedUser"
                  class="pa-3 rounded-lg"
                  style="background-color: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05)">
                  <div class="body-2 font-weight-bold white--text">{{ regManager.selectedUser.vorname }} {{ regManager.selectedUser.nachname }}</div>
                  <div class="caption grey--text">{{ regManager.selectedUser.email }}</div>

                  <div class="d-flex justify-end mt-3">
                    <v-btn
                      color="#009640"
                      small
                      class="white--text font-weight-bold rounded-lg"
                      :loading="regManager.addingUser"
                      @click="submitManualRegistration">
                      Buchen (Admin-Eintrag)
                    </v-btn>
                  </div>
                </div>
              </v-slide-y-transition>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" class="pa-2">
            <div class="pa-4 rounded-xl meta-box text-left" style="height: 100%">
              <h4 class="subtitle-2 font-weight-black white--text text-uppercase mb-2">Kursstatus</h4>
              <div class="d-flex justify-space-between mb-1">
                <span class="caption grey--text">Teilnehmer Plätze:</span>
                <span class="body-2 font-weight-bold white--text" v-if="regManager.course">
                  {{ regManager.registrations.filter((r) => r.status === "registered").length }} / {{ regManager.course.capacity }}
                </span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="caption grey--text">Auf Warteliste:</span>
                <span class="body-2 font-weight-bold orange--text text--lighten-2" v-if="regManager.course">
                  {{ regManager.registrations.filter((r) => r.status === "waitlisted").length }} Personen
                </span>
              </div>
              <div class="d-flex justify-space-between">
                <span class="caption grey--text">Abmeldefrist:</span>
                <span class="body-2 font-weight-bold red--text text--lighten-1" v-if="regManager.course">
                  {{ formatShortDateTime(regManager.course.cancellationDeadline) }}
                </span>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Registrations Table -->
        <h4 class="subtitle-2 font-weight-black white--text text-uppercase mb-2">Teilnehmerliste</h4>
        <v-data-table
          :headers="regHeaders"
          :items="regManager.registrations"
          :loading="regManager.loading"
          class="custom-table"
          dense
          dark
          :items-per-page="10">
          <!-- User Details -->
          <template v-slot:item.user="{ item }">
            <div class="py-1">
              <span class="body-2 font-weight-bold white--text">
                {{ item.user ? `${item.user.vorname} ${item.user.nachname}` : "Gelöschter User" }}
              </span>
              <span class="caption grey--text d-block">{{ item.user ? item.user.email : "" }}</span>
            </div>
          </template>

          <!-- Status badge -->
          <template v-slot:item.status="{ item }">
            <v-chip x-small :color="getRegStatusColor(item)" class="font-weight-bold black--text">
              {{ getRegStatusText(item) }}
            </v-chip>
          </template>

          <!-- Registered At Date -->
          <template v-slot:item.registeredAt="{ item }">
            <span class="caption">{{ formatDateShort(item.registeredAt) }}</span>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-btn v-if="item.status !== 'cancelled'" text x-small color="red" class="font-weight-bold" @click="cancelUserRegistration(item)">
              Stornieren
            </v-btn>
            <span v-else class="caption grey--text italic">Storniert</span>
          </template>
        </v-data-table>

        <div class="d-flex justify-end mt-4">
          <v-btn text color="grey" @click="regManager.show = false" :disabled="regManager.loading">Schliessen</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Confirm Cancel Course Dialog -->
    <v-dialog v-model="cancelCourseDialog.show" max-width="400px" dark>
      <v-card class="dialog-surface pa-4 rounded-xl">
        <h3 class="text-h6 font-weight-bold white--text mb-2">Kurs absagen?</h3>
        <p class="grey--text caption leading-relaxed mb-4">
          Möchtest du den Kurs <strong>{{ cancelCourseDialog.courseTitle }}</strong> wirklich absagen? Alle angemeldeten Mitarbeiter werden
          automatisch storniert und erhalten eine E-Mail-Absage vom System.
        </p>
        <div class="d-flex justify-end gap-3">
          <v-btn text color="grey" @click="cancelCourseDialog.show = false">Abbrechen</v-btn>
          <v-btn color="red" class="black--text font-weight-bold rounded-lg" :loading="cancelCourseDialog.loading" @click="executeCancelCourse">
            Ja, Kurs absagen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </main>
</template>

<script>
import api from "@/api"
import userSearch from "@/components/userSearch.vue"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"

export default {
  name: "EmployeeCoursesAdmin",
  components: {
    userSearch,
    MarketingToolHeader,
  },
  data() {
    return {
      activeTab: "timeslots",
      timeslotsSubTab: "upcoming",
      mobileViewMode: "calendar",
      mobileCalendarDate: new Date().toISOString().split("T")[0],
      loading: true,
      search: "",
      courses: [],
      formats: [],
      studios: [],

      schedulePdfDialog: {
        show: false,
        dates: [],
        downloading: false,
      },

      desktopHeaders: [
        { text: "Zeit", value: "dateTime" },
        { text: "Kursformat & Details", value: "title" },
        { text: "Standort (Studio)", value: "studio" },
        { text: "Belegung", value: "belegung", align: "center" },
        { text: "Warteliste", value: "waitlist", align: "center" },
        { text: "Status", value: "status", align: "center" },
        { text: "Aktionen", value: "actions", sortable: false, align: "right" },
      ],

      formatHeaders: [
        { text: "Formatname", value: "title" },
        { text: "Beschreibung", value: "description", sortable: false },
        { text: "Status", value: "active", align: "center" },
        { text: "Aktionen", value: "actions", sortable: false, align: "right" },
      ],

      courseDialog: {
        show: false,
        isEdit: false,
        valid: false,
        saving: false,
        editId: null,
      },

      courseFormModel: {
        format: "",
        details: "",
        studio: "",
        dateTime: "",
        duration: 60,
        capacity: 20,
        waitlistEnabled: true,
        cancellationDeadline: "",
      },

      formatDialog: {
        show: false,
        isEdit: false,
        valid: false,
        saving: false,
        editId: null,
      },

      formatFormModel: {
        title: "",
        description: "",
        active: true,
      },

      regManager: {
        show: false,
        course: null,
        loading: false,
        registrations: [],
        selectedUserId: null,
        selectedUser: null,
        addingUser: false,
      },

      regHeaders: [
        { text: "Mitarbeiter", value: "user" },
        { text: "Status", value: "status" },
        { text: "Anmeldedatum", value: "registeredAt" },
        { text: "", value: "actions", sortable: false, align: "right" },
      ],

      cancelCourseDialog: {
        show: false,
        courseId: null,
        courseTitle: null,
        loading: false,
      },
    }
  },
  computed: {
    schedulePdfDateRange() {
      const dates = [...new Set((this.schedulePdfDialog.dates || []).filter(Boolean))].sort()
      if (dates.length === 0) return null
      return {
        start: dates[0],
        end: dates[dates.length - 1],
      }
    },

    schedulePdfRangeDays() {
      if (!this.schedulePdfDateRange) return 0
      const start = this.parseLocalDateKey(this.schedulePdfDateRange.start)
      const end = this.parseLocalDateKey(this.schedulePdfDateRange.end)
      if (!start || !end) return 0
      const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
      const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate())
      return Math.round((endUtc - startUtc) / (24 * 60 * 60 * 1000)) + 1
    },

    schedulePdfRangeError() {
      if (!this.schedulePdfDateRange || this.schedulePdfRangeDays < 1) return "Bitte wähle einen gültigen Zeitraum."
      if (this.schedulePdfRangeDays > 366) return "Der Zeitraum darf maximal 366 Tage umfassen."
      return ""
    },

    isSchedulePdfRangeValid() {
      return !!this.schedulePdfDateRange && !this.schedulePdfRangeError
    },

    schedulePdfRangeLabel() {
      if (!this.schedulePdfDateRange) return ""
      const start = this.formatPdfDateKey(this.schedulePdfDateRange.start)
      const end = this.formatPdfDateKey(this.schedulePdfDateRange.end)
      return start === end ? start : `${start} bis ${end}`
    },

    visibleCourses() {
      const now = new Date()
      if (this.timeslotsSubTab === "upcoming") {
        return this.courses.filter((c) => new Date(c.dateTime) >= now).sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))
      } else {
        return this.courses.filter((c) => new Date(c.dateTime) < now).sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))
      }
    },

    // Mobile list view courses (fully filtered by search)
    visibleCoursesFiltered() {
      let list = this.visibleCourses
      if (this.search) {
        const q = this.search.toLowerCase()
        list = list.filter((c) => {
          const titleMatch = c.format && c.format.title && c.format.title.toLowerCase().includes(q)
          const detailsMatch = c.details && c.details.toLowerCase().includes(q)
          const studioMatch = c.studio && c.studio.name && c.studio.name.toLowerCase().includes(q)
          return titleMatch || detailsMatch || studioMatch
        })
      }
      return list
    },

    // Desktop view grouped by day (retains search filters)
    filteredGroupedCourses() {
      let list = this.visibleCoursesFiltered

      const groups = []
      const indexMap = {}

      list.forEach((course) => {
        if (!course.dateTime) return
        const dateObj = new Date(course.dateTime)
        const dateKey = dateObj.toLocaleDateString("de-CH", {
          weekday: "long",
          day: "2-digit",
          month: "long",
          year: "numeric",
        })

        if (indexMap[dateKey] === undefined) {
          indexMap[dateKey] = groups.length
          groups.push({
            date: dateKey,
            courses: [],
          })
        }

        groups[indexMap[dateKey]].courses.push(course)
      })

      return groups
    },

    // Mobile calendar day selected courses list
    mobileSelectedDayCourses() {
      let list = this.visibleCourses.filter((c) => {
        const courseDate = new Date(c.dateTime).toISOString().split("T")[0]
        return courseDate === this.mobileCalendarDate
      })

      if (this.search) {
        const q = this.search.toLowerCase()
        list = list.filter((c) => {
          const titleMatch = c.format && c.format.title && c.format.title.toLowerCase().includes(q)
          const detailsMatch = c.details && c.details.toLowerCase().includes(q)
          const studioMatch = c.studio && c.studio.name && c.studio.name.toLowerCase().includes(q)
          return titleMatch || detailsMatch || studioMatch
        })
      }

      return list
    },

    activeCoursesCount() {
      return this.courses.filter((c) => c.status === "active").length
    },
    totalRegistrationsCount() {
      return this.courses.reduce((sum, c) => sum + (c.registeredCount || 0), 0)
    },
    totalWaitlistCount() {
      return this.courses.reduce((sum, c) => sum + (c.waitlistCount || 0), 0)
    },
    cancellationRate() {
      return 0
    },
    studioSelectOptions() {
      return this.studios.map((s) => ({ text: s.name, value: s._id }))
    },
    activeFormats() {
      return this.formats.filter((f) => f.active === true)
    },
  },
  async created() {
    await this.loadStudios()
    await this.fetchData()
  },
  methods: {
    formatLocalDateKey(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      return `${year}-${month}-${day}`
    },

    parseLocalDateKey(value) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "")
      if (!match) return null
      const year = Number(match[1])
      const month = Number(match[2])
      const day = Number(match[3])
      const date = new Date(year, month - 1, day)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null
      return date
    },

    formatPdfDateKey(value) {
      const date = this.parseLocalDateKey(value)
      if (!date) return ""
      return date.toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    },

    openSchedulePdfDialog() {
      if (!this.schedulePdfDialog.dates.length) {
        const start = new Date()
        const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 30)
        this.schedulePdfDialog.dates = [this.formatLocalDateKey(start), this.formatLocalDateKey(end)]
      }
      this.schedulePdfDialog.show = true
    },

    async downloadSchedulePdf() {
      if (!this.isSchedulePdfRangeValid) return

      const range = this.schedulePdfDateRange
      const filename = `Mitarbeiterkurse_Einsatzplan_${range.start}_bis_${range.end}.pdf`

      this.schedulePdfDialog.downloading = true
      try {
        await api.employeeCourses.admin.downloadSchedulePdf(range.start, range.end, filename)
        this.$toast.success("Einsatzplan wurde erstellt.")
        this.schedulePdfDialog.show = false
      } catch (error) {
        console.error(error)
        this.$toast.error("Der Einsatzplan konnte nicht erstellt werden.")
      } finally {
        this.schedulePdfDialog.downloading = false
      }
    },

    isPastCourse(item) {
      if (!item || !item.dateTime) return false
      return new Date(item.dateTime) < new Date()
    },

    async loadStudios() {
      try {
        let studios = await api.fetchStudios()
        this.studios = studios
          .filter((s) => s.code && s.code !== "ot" && s.code !== "alibey")
          .map((s) => ({ _id: s._id, name: s.name, code: s.code }))
      } catch (e) {
        console.error("Failed to load studios:", e)
        this.studios = [
          { _id: "5f3c30bfd91ddb464c2648b2", name: "Frauenfeld Zentral", code: "frauenfeld" },
          { _id: "5f3c30bfd91ddb464c2648b3", name: "St. Gallen West", code: "stgallen" },
        ]
      }
    },

    async fetchData() {
      this.loading = true
      try {
        const [coursesData, formatsData] = await Promise.all([api.employeeCourses.admin.fetchCourses(), api.employeeCourses.admin.fetchFormats()])
        this.courses = coursesData || []
        this.formats = formatsData || []
      } catch (error) {
        console.error(error)
        this.$toast.error("Daten konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },

    formatTime(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleTimeString("de-CH", {
        hour: "2-digit",
        minute: "2-digit",
      })
    },

    formatDateShort(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    },

    formatDateShortCompact(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
      })
    },

    formatLocalDateString(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("de-CH", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    },

    formatShortDateTime(dateStr) {
      if (!dateStr) return ""
      return (
        new Date(dateStr).toLocaleDateString("de-CH", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }) + " Uhr"
      )
    },

    formatDateTimeLocal(dateVal) {
      if (!dateVal) return ""
      const d = new Date(dateVal)
      if (isNaN(d.getTime())) return ""
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      const hours = String(d.getHours()).padStart(2, "0")
      const minutes = String(d.getMinutes()).padStart(2, "0")
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },

    onDateTimeInput(value) {
      if (!value || this.courseDialog.isEdit) return
      const selectedDate = new Date(value)
      if (isNaN(selectedDate.getTime())) return

      const twoHoursBefore = new Date(selectedDate.getTime() - 2 * 60 * 60 * 1000)
      this.courseFormModel.cancellationDeadline = this.formatDateTimeLocal(twoHoursBefore)
    },

    // Mobile calendar events dots
    getCalendarEvents(dateStr) {
      return this.courses.some((c) => {
        const courseDate = new Date(c.dateTime).toISOString().split("T")[0]
        return courseDate === dateStr
      })
    },

    // --- Course Formats Actions ---
    openCreateFormatDialog() {
      this.formatFormModel = {
        title: "",
        description: "",
        active: true,
      }
      this.formatDialog.isEdit = false
      this.formatDialog.editId = null
      this.formatDialog.show = true
      this.$nextTick(() => {
        if (this.$refs.formatForm) this.$refs.formatForm.resetValidation()
      })
    },

    openEditFormatDialog(format) {
      this.formatFormModel = {
        title: format.title,
        description: format.description || "",
        active: format.active,
      }
      this.formatDialog.isEdit = true
      this.formatDialog.editId = format._id
      this.formatDialog.show = true
      this.$nextTick(() => {
        if (this.$refs.formatForm) this.$refs.formatForm.resetValidation()
      })
    },

    async submitFormatForm() {
      this.formatDialog.saving = true
      try {
        if (this.formatDialog.isEdit) {
          await api.employeeCourses.admin.updateFormat(this.formatDialog.editId, this.formatFormModel)
          this.$toast.success("Kursformat aktualisiert.")
        } else {
          await api.employeeCourses.admin.createFormat(this.formatFormModel)
          this.$toast.success("Kursformat erfolgreich erstellt.")
        }
        this.formatDialog.show = false
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Speichern fehlgeschlagen.")
      } finally {
        this.formatDialog.saving = false
      }
    },

    // --- Course Timeslots Actions ---
    openCreateDialog() {
      let defaultStudioId = ""
      if (this.studios.length > 0) {
        const frauenfeldStudio = this.studios.find((s) => s.code === "frauenfeld")
        defaultStudioId = frauenfeldStudio ? frauenfeldStudio._id : this.studios[0]._id
      }

      this.courseFormModel = {
        format: this.activeFormats.length > 0 ? this.activeFormats[0]._id : "",
        details: "",
        studio: defaultStudioId,
        dateTime: "",
        duration: 60,
        capacity: 20,
        waitlistEnabled: true,
        cancellationDeadline: "",
      }
      this.courseDialog.isEdit = false
      this.courseDialog.editId = null
      this.courseDialog.show = true
      this.$nextTick(() => {
        if (this.$refs.courseForm) this.$refs.courseForm.resetValidation()
      })
    },

    openEditDialog(course) {
      this.courseFormModel = {
        format: course.format ? course.format._id : "",
        details: course.details || "",
        studio: course.studio ? course.studio._id : "",
        dateTime: this.formatDateTimeLocal(course.dateTime),
        duration: course.duration,
        capacity: course.capacity,
        waitlistEnabled: course.waitlistEnabled,
        cancellationDeadline: this.formatDateTimeLocal(course.cancellationDeadline),
      }
      this.courseDialog.isEdit = true
      this.courseDialog.editId = course._id
      this.courseDialog.show = true
      this.$nextTick(() => {
        if (this.$refs.courseForm) this.$refs.courseForm.resetValidation()
      })
    },

    async submitCourseForm() {
      this.courseDialog.saving = true
      try {
        if (this.courseDialog.isEdit) {
          await api.employeeCourses.admin.updateCourse(this.courseDialog.editId, this.courseFormModel)
          this.$toast.success("Kurs erfolgreich aktualisiert.")
        } else {
          await api.employeeCourses.admin.createCourse(this.courseFormModel)
          this.$toast.success("Kurs erfolgreich geplant.")
        }
        this.courseDialog.show = false
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Speichern des Kurses fehlgeschlagen.")
      } finally {
        this.courseDialog.saving = false
      }
    },

    confirmCancelCourse(course) {
      const title = course.format ? course.format.title : "Kurs"
      this.cancelCourseDialog = {
        show: true,
        courseId: course._id,
        courseTitle: title,
        loading: false,
      }
    },

    async executeCancelCourse() {
      this.cancelCourseDialog.loading = true
      try {
        await api.employeeCourses.admin.deleteCourse(this.cancelCourseDialog.courseId)
        this.$toast.success("Kurs erfolgreich abgesagt.")
        this.cancelCourseDialog.show = false
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Absage fehlgeschlagen.")
      } finally {
        this.cancelCourseDialog.loading = false
      }
    },

    // --- Registrations Manager ---
    async openManageRegistrations(course) {
      this.regManager.course = course
      this.regManager.registrations = []
      this.regManager.selectedUserId = null
      this.regManager.selectedUser = null
      this.regManager.show = true
      await this.fetchRegistrations()
    },

    async fetchRegistrations() {
      if (!this.regManager.course) return
      this.regManager.loading = true
      try {
        const data = await api.employeeCourses.admin.fetchRegistrations(this.regManager.course._id)
        this.regManager.registrations = data || []
      } catch (e) {
        console.error(e)
        this.$toast.error("Teilnehmer konnten nicht geladen werden.")
      } finally {
        this.regManager.loading = false
      }
    },

    async onUserSelected(userId) {
      if (!userId) {
        this.regManager.selectedUserId = null
        this.regManager.selectedUser = null
        return
      }

      this.regManager.selectedUserId = userId
      try {
        const userDetails = await api.employeeCourses.admin.fetchUserDetails(userId)
        this.regManager.selectedUser = userDetails
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden der Mitarbeiterdetails.")
        this.regManager.selectedUserId = null
        this.regManager.selectedUser = null
      }
    },

    async submitManualRegistration() {
      if (!this.regManager.selectedUserId || !this.regManager.course) return
      this.regManager.addingUser = true
      try {
        await api.employeeCourses.admin.manualRegister(this.regManager.course._id, this.regManager.selectedUserId)
        this.$toast.success("Mitarbeiter erfolgreich eingebucht.")
        this.regManager.selectedUserId = null
        this.regManager.selectedUser = null
        await this.fetchRegistrations()
        await this.fetchData()
      } catch (error) {
        console.error(error)
        this.$toast.error(error.response?.data || "Manuelle Buchung fehlgeschlagen.")
      } finally {
        this.regManager.addingUser = false
      }
    },

    async cancelUserRegistration(item) {
      if (!this.regManager.course) return
      this.regManager.loading = true
      try {
        await api.employeeCourses.admin.cancelRegistration(this.regManager.course._id, item._id)
        this.$toast.success("Buchung storniert.")
        await this.fetchRegistrations()
        await this.fetchData()
      } catch (e) {
        console.error(e)
        this.$toast.error("Stornierung fehlgeschlagen.")
      } finally {
        this.regManager.loading = false
      }
    },

    getRegStatusColor(item) {
      if (item.status === "registered") return "green accent-3"
      if (item.status === "waitlisted") return "orange"
      return "grey"
    },

    getRegStatusText(item) {
      if (item.status === "registered") return "Gebucht"
      if (item.status === "waitlisted") return `Warteliste (${item.waitlistPosition})`
      return "Storniert"
    },
  },
}
</script>

<style scoped>
.courses-admin-shell {
  background-color: #0f0f0f;
  min-height: 100vh;
  color: white;
}

.admin-container {
  max-width: 1200px;
  margin: 0 auto;
}

.italic-title {
  font-style: italic;
  font-weight: 950;
  letter-spacing: -1px;
}

/* Cards */
.stat-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3) !important;
}

/* Table Card */
.table-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.search-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.search-field :deep(.v-select__selection) {
  color: white !important;
}

.search-field :deep(.v-icon) {
  color: #fbc02d !important;
}

/* Custom Table Styles */
.custom-table :deep(table) {
  background-color: transparent !important;
}

.custom-table :deep(thead th) {
  font-weight: bold !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  letter-spacing: 0.5px;
  color: #777777 !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.08) !important;
}

.custom-table :deep(tbody td) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.custom-table :deep(tbody tr:hover) {
  background-color: rgba(255, 255, 255, 0.02) !important;
}

.border-thin-outline {
  border: 1px solid rgba(255, 255, 255, 0.04) !important;
  border-radius: 8px !important;
}

.date-header-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #fbc02d;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(251, 192, 45, 0.6);
}

.meta-box {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.border-accent-box {
  border: 1px dashed rgba(251, 192, 45, 0.3) !important;
}

/* Mobile Styling */
.mobile-course-card {
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  transition: all 0.2s ease;
}

.mobile-course-card.is-past {
  opacity: 0.7;
}

.mobile-course-card.is-cancelled {
  border-color: rgba(244, 67, 54, 0.3) !important;
  background-color: rgba(244, 67, 54, 0.02) !important;
}

.divider-thin {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.06);
}

.empty-state-mobile {
  background-color: #1c1c1e;
  border: 1px dashed rgba(255, 255, 255, 0.08);
}

.width-100 {
  width: 100%;
}

.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
.gap-4 {
  gap: 16px;
}

/* Academy-aligned page system */
.courses-admin-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.courses-admin-shell {
  width: 100%;
  max-width: 92.5rem;
  min-height: auto;
  margin: 0 auto;
  color: var(--text);
  background: transparent;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.admin-navigation {
  margin: 1rem 0;
  padding: 0.375rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem;
  background: rgba(255, 255, 255, 0.025);
}

.admin-navigation ::v-deep .v-tabs-bar {
  height: 3.25rem;
  border-radius: 0.8125rem;
  background: transparent !important;
}

.admin-navigation ::v-deep .v-tab {
  min-height: 2.75rem;
  border-radius: 0.75rem;
  color: var(--muted) !important;
  font-size: 0.875rem;
  letter-spacing: 0;
  text-transform: none;
}

.admin-navigation ::v-deep .v-tab--active {
  color: #ffffff !important;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.16), rgba(255, 255, 255, 0.035));
}

.admin-navigation ::v-deep .v-tabs-slider-wrapper {
  display: none;
}

.section-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.075), transparent 52%), rgba(255, 255, 255, 0.03);
}

.section-heading {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 0.875rem;
}

.section-heading > div {
  min-width: 0;
}

.section-icon,
.format-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
}

.section-icon {
  width: 2.875rem;
  height: 2.875rem;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 0.875rem;
}

.section-icon .v-icon,
.format-icon .v-icon {
  color: currentColor;
}

.section-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.section-heading h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.5rem;
  line-height: 1.25;
}

.section-heading p {
  max-width: 38rem;
  margin: 0.375rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.55;
}

.section-actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.625rem;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.metric-card {
  display: flex;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
  box-shadow: none !important;
}

.metric-icon {
  display: grid;
  flex: 0 0 2.625rem;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.metric-icon .v-icon {
  color: currentColor;
}

.icon-green {
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
}

.icon-calendar {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.11);
}

.icon-waiting {
  color: #f4c95d;
  background: rgba(244, 201, 93, 0.11);
}

.metric-card > div {
  display: grid;
  min-width: 0;
}

.metric-card > div > span:first-child {
  color: var(--text-soft);
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  color: #ffffff;
  font-size: 1.6875rem;
  line-height: 1.15;
}

.metric-card small {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.table-card {
  padding: 1.125rem;
  border: 1px solid var(--border) !important;
  border-radius: 1.25rem !important;
  background: var(--surface) !important;
  box-shadow: none !important;
}

.custom-table {
  border-radius: 0.875rem;
  overflow: hidden;
  background: transparent !important;
}

.custom-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.custom-table ::v-deep table {
  min-width: 54rem;
}

.custom-table ::v-deep thead th {
  color: #8f9095 !important;
  font-size: 0.75rem !important;
  letter-spacing: 0.04em;
}

.custom-table ::v-deep tbody td {
  color: var(--text-soft);
  font-size: 0.875rem;
}

.border-thin-outline {
  border-color: var(--border) !important;
  border-radius: 0.875rem !important;
}

.date-header-dot {
  background-color: #58d388;
  box-shadow: 0 0 0.625rem rgba(88, 211, 136, 0.42);
}

.courses-admin-page .yellow--text.text--darken-2,
.dialog-surface .yellow--text.text--darken-2 {
  color: #58d388 !important;
}

.search-field ::v-deep .v-input__slot {
  min-height: 2.875rem;
  border-radius: 0.8125rem !important;
}

.search-field ::v-deep .v-icon {
  color: #58d388 !important;
}

.mobile-subtabs,
.mobile-course-card,
.empty-state-mobile {
  border: 1px solid var(--border);
  border-radius: 1.0625rem !important;
  background: var(--surface) !important;
}

.mobile-subtabs {
  padding: 0.25rem;
}

.mobile-subtabs ::v-deep .v-tab {
  min-height: 2.75rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  letter-spacing: 0;
  text-transform: none;
}

.mobile-course-list {
  display: grid;
  gap: 0.75rem;
}

.mobile-course-card {
  padding: 1rem !important;
}

.mobile-course-card.is-past {
  opacity: 0.72;
}

.mobile-course-card > .d-flex.justify-space-between.align-center {
  flex-wrap: wrap;
  gap: 0.625rem;
}

.mobile-course-card .v-btn {
  width: 2.75rem;
  min-width: 2.75rem !important;
  height: 2.75rem;
}

.mobile-course-card .caption,
.empty-state-mobile .caption {
  font-size: 0.75rem !important;
}

.courses-admin-page ::v-deep .v-chip.v-size--small,
.courses-admin-page ::v-deep .v-chip.v-size--x-small,
.dialog-surface ::v-deep .v-chip.v-size--x-small {
  min-height: 1.75rem;
  font-size: 0.75rem !important;
}

.format-card-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.format-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid var(--border) !important;
  border-radius: 1.0625rem !important;
  background: var(--surface) !important;
}

.format-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.format-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
}

.format-card h3 {
  margin: 0.875rem 0 0.375rem;
  color: #ffffff;
  font-size: 1rem;
  line-height: 1.4;
}

.format-card p {
  flex: 1 1 auto;
  margin: 0 0 1rem;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.55;
}

.empty-state-mobile {
  display: grid;
  min-height: 12rem;
  grid-column: 1 / -1;
  place-content: center;
  gap: 0.5rem;
  padding: 1rem;
  color: var(--muted);
  text-align: center;
}

.empty-state-mobile strong {
  color: #ffffff;
  font-size: 1rem;
}

.empty-state-mobile span {
  font-size: 0.875rem;
}

.dialog-surface {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  border: 1px solid var(--border) !important;
  color: var(--text);
  background: #1a1a1c !important;
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.42) !important;
}

.dialog-surface .caption {
  font-size: 0.75rem !important;
}

.dialog-surface .v-btn {
  min-height: 2.75rem;
  font-size: 0.875rem;
  letter-spacing: 0;
  text-transform: none;
}

.dialog-surface .custom-table ::v-deep table {
  min-width: 40rem;
}

.meta-box,
.border-accent-box {
  border-color: var(--border) !important;
  background: rgba(255, 255, 255, 0.03) !important;
}

@media (max-width: 78.9375rem) {
  .courses-admin-page {
    padding: 1.125rem;
  }
}

@media (max-width: 59.9375rem) {
  .section-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .section-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mobile-course-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .courses-admin-page {
    padding: 0.75rem;
  }

  .admin-navigation {
    margin: 0.75rem 0;
    padding: 0.25rem;
    border-radius: 1rem;
  }

  .admin-navigation ::v-deep .v-tab {
    padding: 0 0.5rem;
  }

  .admin-navigation ::v-deep .v-tab .v-icon {
    margin-right: 0.375rem;
  }

  .section-toolbar {
    padding: 0.875rem;
    border-radius: 1.0625rem;
  }

  .section-actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
  }

  .section-actions .v-btn {
    width: 100%;
    margin: 0;
  }

  .metric-grid {
    gap: 0.5rem;
  }

  .metric-card {
    min-height: 7rem;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 0.9375rem !important;
  }

  .metric-icon {
    width: 2.25rem;
    height: 2.25rem;
    flex-basis: 2.25rem;
  }

  .mobile-course-list,
  .format-card-list {
    grid-template-columns: minmax(0, 1fr);
  }

  .mobile-course-card > .d-flex.justify-space-between.align-center.mb-3 {
    align-items: flex-start !important;
    flex-direction: column;
  }

  .dialog-surface {
    min-height: 100vh;
    min-height: 100dvh;
    padding: 1rem !important;
    border: 0 !important;
    border-radius: 0 !important;
  }

  .dialog-surface > .d-flex.justify-end {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .dialog-surface > .d-flex.justify-end .v-btn {
    width: 100%;
    margin: 0;
  }
}

@media (max-width: 22.5rem) {
  .metric-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .metric-card {
    min-height: 5.75rem;
    align-items: center;
    flex-direction: row;
  }
}
</style>
